/**
 * Leave Policy Assistant — in-app chat client.
 *
 * Second transport for the same grounded policy Q&A the Slack Leave Assistant
 * serves. Posts the question plus recent conversation turns to
 * `POST /api/v1/assistant/ask`; the api loads the leave policy and hops to
 * ai-service. Memory is session-only, so the caller owns `history` and replays
 * it each turn.
 */
import { z } from 'zod'

import { apiRequest } from './client'

export type AssistantRole = 'user' | 'assistant'

export interface AssistantTurn {
  role: AssistantRole
  content: string
}

// Zod at the boundary — the answer is model-generated text; validate before use.
const assistantAnswerSchema = z.object({ answer: z.string().min(1) })

const assistantTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
})
const assistantConversationSchema = z.object({ turns: z.array(assistantTurnSchema) })

/**
 * Ask the assistant a leave-policy question.
 *
 * @param history recent turns (oldest first); the server trims to its own cap.
 * @returns the grounded answer text (never blank — falls back to "ask HR").
 */
export async function askAssistant(
  question: string,
  history: readonly AssistantTurn[],
  signal?: AbortSignal,
): Promise<string> {
  const raw = await apiRequest<unknown>('/api/v1/assistant/ask', {
    method: 'POST',
    body: { question, history },
    ...(signal ? { signal } : {}),
  })
  return assistantAnswerSchema.parse(raw).answer
}

/**
 * Load the stored conversation (Redis-backed, ~1 week TTL) so the chat restores
 * on open. Returns [] for a cold thread or if the cache has aged out.
 */
export async function getConversation(): Promise<AssistantTurn[]> {
  const raw = await apiRequest<unknown>('/api/v1/assistant/conversation')
  return assistantConversationSchema.parse(raw).turns
}

/** Clear the stored conversation (the chat's reset action). */
export async function clearConversation(): Promise<void> {
  await apiRequest<unknown>('/api/v1/assistant/conversation', { method: 'DELETE' })
}
